import { useEffect, useId, useRef, useState } from 'react'
import { usePublicationContextOptional } from '@/app/state/PublicationContext'
import { AddContentModal } from '@/app/content-intake/AddContentModal'
import type { ContentIntakeMode } from '@domain/content-intake'

const ACTIONS: Array<{ id: ContentIntakeMode; label: string; description: string }> = [
  {
    id: 'url',
    label: 'Add URL',
    description: 'Pull article content from a website.',
  },
  {
    id: 'paste',
    label: 'Paste Content',
    description: 'Enter content Spox should work from.',
  },
  {
    id: 'seo_package',
    label: 'Paste SEO Package',
    description: 'Paste an external SEO/social package for intake.',
  },
]

interface AddContentMenuProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

/** Global "+ Add Content" entry point — SPX-019L opens the compact intake
 * modal directly in the chosen mode, instead of navigating to a full page. */
export function AddContentMenu({ open: openExternal, onOpenChange }: AddContentMenuProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = openExternal ?? internalOpen
  const setOpen = onOpenChange ?? setInternalOpen
  const menuId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const activePublication = usePublicationContextOptional()?.activePublication ?? null
  const [modalMode, setModalMode] = useState<ContentIntakeMode | null>(null)

  useEffect(() => {
    if (!open) return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    function onPointer(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onPointer)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onPointer)
    }
  }, [open, setOpen])

  return (
    <div className="add-content" ref={rootRef} data-testid="add-content-menu">
      <button
        type="button"
        className="btn compact add-content__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        data-testid="add-content-trigger"
        onClick={() => setOpen(!open)}
      >
        + Add Content
      </button>
      {open ? (
        <div
          className="add-content__panel"
          id={menuId}
          role="menu"
          aria-label="Add content methods"
        >
          {ACTIONS.map((action) => (
            <button
              key={action.id}
              type="button"
              role="menuitem"
              className="add-content__item"
              data-testid={`add-content-${action.id}`}
              onClick={() => {
                setOpen(false)
                setModalMode(action.id)
              }}
            >
              <span className="add-content__item-label">{action.label}</span>
              <span className="add-content__item-desc">{action.description}</span>
            </button>
          ))}
        </div>
      ) : null}

      {modalMode && activePublication ? (
        <AddContentModal
          publicationId={activePublication.id}
          initialMode={modalMode}
          onClose={() => {
            setModalMode(null)
            // Focus returns to "+ Add Content" after close (accessibility requirement).
            rootRef.current?.querySelector<HTMLButtonElement>('[data-testid="add-content-trigger"]')?.focus()
          }}
        />
      ) : null}
    </div>
  )
}

/** Imperative helper for empty-state "Add Content" buttons elsewhere in the
 * app (e.g. Sources' empty state) — unchanged legacy behaviour, see SPX-019L
 * completion report on why this intentionally still targets the legacy route
 * rather than being rewired to the new modal in this branch. */
export function openAddContentViaNavigate(navigate: (path: string) => void) {
  navigate('/sources/new?mode=paste')
}
